package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.budget.InvalidLabourBudgetException
import com.evoapps.evoshift.domain.budget.LabourBudget
import com.evoapps.evoshift.domain.budget.ShiftCostEstimate
import com.evoapps.evoshift.domain.budget.TeamCostRate
import com.evoapps.evoshift.domain.budget.isUnfilledDemand
import com.evoapps.evoshift.domain.budget.MissingShiftCosts
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.coroutines.awaitAll
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.LabourBudgetRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.model.isPaidFor
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

/**
 * SH-115: what the roster builder needs for the budget and the shifts' costs: the budget
 * ([LabourBudget.OFF] while none is set: the costs still show) and what shifts cost.
 */
data class RosterBudgetContext(
    val budget: LabourBudget,
    /** Prices the month's shifts and changes from its cost snapshots. */
    val estimate: ShiftCostEstimate,
    /**
     * SH-210: the month's paid interviews as their participants' shifts (in memory only): the
     * budget counts them with the grid's shifts.
     */
    val interviewShifts: List<com.evoapps.evoshift.domain.model.Shift> = emptyList()
)

/**
 * SH-115: a workplace's labour budget. Money, so null for those without VIEW_FINANCES (owners and
 * financial managers have it), and nothing is read for them; firestore.rules deny the others too.
 */
class GetLabourBudgetUseCase(
    private val repository: LabourBudgetRepository,
    private val financialRepository: FinancialRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val caller: CallerCapabilities? = null,
    /** Prices unassigned shifts while the month has no cost snapshot; without it they stay unpriced then. */
    private val teamCostRate: GetTeamCostRateUseCase? = null,
    /** Pay terms for shifts without a usable snapshot, priced on the fly (MissingShiftCosts). */
    private val userRepository: UserRepository? = null,
    /** SH-210: paid interviews are priced like their participants' shifts. */
    private val interviewRepository: com.evoapps.evoshift.domain.repository.InterviewRepository? = null,
    /** SH-206: who counts in the labour cost; without it the defaults of before apply. */
    private val labourInclusion: com.evoapps.evoshift.domain.budget.LabourInclusionReader? = null
) {
    /** The budget of [workplaceId] ([LabourBudget.OFF] while none is set); null without VIEW_FINANCES. */
    suspend operator fun invoke(workplaceId: String): Result<LabourBudget?> = runCatching {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        repository.get(workplaceId).getOrThrow().normalized()
    }

    /**
     * The roster month's budget with the cost of its shifts ([ShiftCostEstimate]): the shift
     * dialogs show each shift's cost to money roles with the budget off too. Null without
     * VIEW_FINANCES (then nothing else is read).
     */
    suspend fun forRoster(rosterId: String): Result<RosterBudgetContext?> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val budget = invoke(workplaceId).getOrThrow() ?: return@runCatching null
        coroutineScope {
            val shifts = async { shiftRepository.getShiftsForRoster(rosterId).getOrThrow() }
            val snapshots = async { financialRepository.getShiftFinancialsForRoster(rosterId).getOrThrow() }
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrNull() }
            // SH-210: paid interviews as their participants' shifts (in memory only), priced as such.
            val interviews = interviewRepository?.getForRoster(rosterId)?.getOrNull()
                ?.flatMap { it.labourShifts() }.orEmpty()
            val monthShifts = shifts.await() + interviews
            // An unpaid owner's shifts are not paid work (CalculateShiftCostUseCase): they cost nothing.
            val wp = workplace.await()
            val (year, month) = RosterIds.yearMonthOf(rosterId)
            // SH-232: paid work by the shift's own day; the people with no paid day this month at all.
            val monthId = com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(year, month)
            val unpaidShifts = wp?.let { w -> monthShifts.filter { s -> s.userId?.let { !w.isPaidFor(it, s) } == true }.map { it.id }.toSet() }.orEmpty()
            val notPaidWork = wp?.let { w -> monthShifts.mapNotNull { it.userId }.distinct().filterNot { w.isPaidIn(it, monthId) }.toSet() }.orEmpty()
            // Shifts without a usable snapshot priced from their people's pay terms, as the day's labour cost does.
            val stored = snapshots.await()
            val missing = MissingShiftCosts.unpriced(monthShifts.filter { it.id !in unpaidShifts }, stored.associateBy { it.shiftId }) { it !in notPaidWork }
            val users = userRepository
            var unpaid = notPaidWork
            var engagements = emptyMap<String, com.evoapps.evoshift.domain.model.FinancialEngagement>()
            val priced = if (missing.isEmpty() || users == null || wp == null) stored else {
                engagements = MissingShiftCosts.engagementsOf(users, workplaceId, missing.mapNotNull { it.workerId })
                // SH-147: the owner without pay terms works without a wage (not at the month's average).
                unpaid = unpaid + MissingShiftCosts.ownerWithoutTerms(wp.ownerId, missing, engagements)
                MissingShiftCosts.completed(stored, missing, monthShifts, engagements, wp.settings)
            }
            // SH-206: the people who do not count add 0; those counted without a wage too.
            val people = labourInclusion?.takeIf { wp != null }
                ?.people(wp!!, monthShifts.mapNotNull { it.userId }, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(year, month), engagements)
                ?.getOrNull() ?: com.evoapps.evoshift.domain.budget.LabourPeople.UNKNOWN
            unpaid = unpaid + people.countedWithoutWage
            val excluded = people.excluded
            val estimate = ShiftCostEstimate.of(monthShifts, priced, unpaid, excludedUserIds = excluded, unpaidShiftIds = unpaidShifts)
            // A month with no snapshot yet (just built): the team's pay terms price its shifts.
            val team = if (estimate.averageCostPerHour == null && monthShifts.any { it.isUnfilledDemand || it.userId != null }) {
                teamCostRate?.invoke(rosterId)?.getOrNull()
            } else null
            // SH-193: unassigned shifts at the workplace's chosen rate, as in the daily records and totals.
            val previousMonthId = if (month == 1) RosterIds.of(workplaceId, year - 1, 12) else RosterIds.of(workplaceId, year, month - 1)
            val previous = financialRepository.getDailyRecordsForRoster(previousMonthId).getOrDefault(emptyList())
            val openRate = com.evoapps.evoshift.domain.budget.OpenShiftRates.resolve(
                wp?.settings?.payroll?.openShiftCost ?: com.evoapps.evoshift.domain.market.payroll.OpenShiftCostSetting(),
                com.evoapps.evoshift.domain.budget.OpenShiftRates.ofMonth(previous),
                estimate.averageCostPerHour ?: team
            )
            // SH-210 + SH-206: a paid interview of someone who does not count adds nothing either.
            RosterBudgetContext(
                budget, ShiftCostEstimate.of(monthShifts, priced, unpaid, team, openRate, excluded, unpaidShifts),
                interviews.filterNot { it.userId in excluded }
            )
        }
    }
}

/**
 * SH-115: saves a workplace's labour budget (off: [LabourBudget.OFF]). Amounts must be above
 * zero; a weekly budget only counts with a monthly one. firestore.rules allow it to the owner and
 * financial managers while the plan runs.
 */
class SaveLabourBudgetUseCase(private val repository: LabourBudgetRepository) {
    suspend operator fun invoke(workplaceId: String, budget: LabourBudget, updatedBy: String): Result<LabourBudget> = runCatching {
        val kept = budget.normalized()
        listOfNotNull(kept.monthly, kept.weekly).firstOrNull { !LabourBudget.isValidAmount(it) }?.let { throw InvalidLabourBudgetException(it) }
        repository.save(workplaceId, kept, updatedBy).getOrThrow()
        kept
    }
}

/**
 * The team's average employer cost per hour at a roster's workplace from their pay terms
 * ([TeamCostRate]): what unassigned shifts are estimated at before the month has any cost snapshot.
 * Money, so null without VIEW_FINANCES; people whose pay terms cannot be read are left out.
 */
class GetTeamCostRateUseCase(
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val caller: CallerCapabilities? = null,
    /** SH-206: people who do not count in the labour cost stay out of the average. */
    private val labourInclusion: com.evoapps.evoshift.domain.budget.LabourInclusionReader? = null
) {
    suspend operator fun invoke(rosterId: String): Result<Double?> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val people = async { userRepository.getAssignableEmployees(workplaceId).getOrThrow() }
            val wp = workplace.await()
            // SH-232: people with paid work here this month.
            val paid = people.await().filter { wp.isPaidIn(it.uid, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(year, month)) }
            val terms = paid.map { person ->
                async { userRepository.getSensitiveUserData(person.uid).getOrNull()?.financialEngagements?.get(workplaceId)?.let { person.uid to it } }
            }.awaitAll().filterNotNull().toMap()
            val excluded = labourInclusion
                ?.people(wp, paid.map { it.uid }, com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(year, month), terms)
                ?.getOrNull()?.excluded.orEmpty()
            TeamCostRate.of(terms.filterKeys { it !in excluded }.values, wp.settings, year, month)
        }
    }
}
